/**
 * The account menu: who you are, your preferences, and the way out.
 *
 * Everything in it existed before — the name, email and "Platform
 * Administrator" line, "Sign out", and the version and environment the
 * sidebar showed in its footer beside the link to the tenant app. The theme
 * and table-density switches are preferences of this browser
 * (src/lib/theme.js) and change nothing on the server.
 */
import { Avatar } from '../components/Layout.jsx';
import { Icon } from '../components/Icon.jsx';
import { Popover } from '../components/Popover.jsx';
import { useAdminAuth } from '../context/AdminAuthContext.jsx';
import { useAppearance } from '../hooks/useAppearance.js';
import { tenantAppUrl } from '../lib/tenantApp.js';

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', icon: 'sun' },
  { value: 'dark', label: 'Dark', icon: 'moon' },
  { value: 'system', label: 'System', icon: 'monitor' },
];

/** The light / dark / system switch. */
export function ThemeSwitch() {
  const { preference, setTheme } = useAppearance();
  return (
    <div className="segmented" role="radiogroup" aria-label="Theme">
      {THEME_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={preference === option.value}
          className="segmented-option"
          onClick={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            setTheme(option.value, { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
          }}
          title={`${option.label} theme`}
        >
          <Icon name={option.icon} size={14} />
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  );
}

function DensitySwitch() {
  const { density, setDensity } = useAppearance();
  return (
    <div className="segmented" role="radiogroup" aria-label="Table density">
      {[
        ['comfortable', 'Comfortable'],
        ['compact', 'Compact'],
      ].map(([value, label]) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={density === value}
          className="segmented-option"
          onClick={() => setDensity(value)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function UserMenu({ onShowShortcuts }) {
  const { user, signOut } = useAdminAuth();

  return (
    <Popover
      label="Account"
      align="end"
      panelClassName="popover-account"
      trigger={({ triggerProps, open }) => (
        <button
          type="button"
          className={`topbar-account ${open ? 'is-open' : ''}`}
          {...triggerProps}
          aria-label={`Account: ${user?.fullName ?? user?.email ?? ''}`}
          title={user?.fullName ?? user?.email}
        >
          <Avatar name={user?.fullName} email={user?.email} />
        </button>
      )}
    >
      {({ close }) => (
        <div className="account">
          <div className="account-head">
            <Avatar name={user?.fullName} email={user?.email} large />
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="strong truncate">{user?.fullName}</div>
              <div className="text-sm muted truncate">{user?.email}</div>
              <div className="account-role">Platform Administrator</div>
            </div>
          </div>

          <div className="account-prefs">
            <div className="account-pref">
              <span className="account-pref-label">Theme</span>
              <ThemeSwitch />
            </div>
            <div className="account-pref">
              <span className="account-pref-label">Tables</span>
              <DensitySwitch />
            </div>
          </div>

          <div className="account-links">
            <a
              href={tenantAppUrl()}
              className="account-link"
              target="_blank"
              rel="noreferrer"
              onClick={() => close({ restoreFocus: false })}
            >
              <Icon name="external-link" size={15} />
              Open the tenant app
            </a>
            <button
              type="button"
              className="account-link"
              onClick={() => {
                close({ restoreFocus: false });
                onShowShortcuts?.();
              }}
            >
              <Icon name="keyboard" size={15} />
              Keyboard shortcuts
            </button>
          </div>

          <div className="account-links">
            <button type="button" className="account-link account-link-danger" onClick={signOut}>
              <Icon name="log-out" size={15} />
              Sign out
            </button>
          </div>

          <div className="account-foot">v1.0.0 · {import.meta.env.VITE_ENVIRONMENT ?? 'development'}</div>
        </div>
      )}
    </Popover>
  );
}
